import React, { useCallback, useRef, useState } from "react";
import { Download, Loader2 } from "lucide-react";
import { resumeData } from "../data/resume";
import { useInView } from "../hooks/useInView";
import { usePdfExport } from "../hooks/usePdfExport";
import ResumeHeader from "./resume/ResumeHeader";
import LeftColumn from "./resume/LeftColumn";
import RightColumn from "./resume/RightColumn";
import VideosSection from "./resume/VideosSection";
import VideoModal from "./resume/VideoModal";

const Resume = () => {
  const { name, title, photo, contact } = resumeData;
  const [skillsRef, skillsInView] = useInView(0.3);
  const [forceBars, setForceBars] = useState(false);
  const [activeVideo, setActiveVideo] = useState(null);
  const sheetRef = useRef(null);

  const barsIn = skillsInView || forceBars;

  // Ensure skill bars are painted before the PDF capture runs.
  const onBeforeCapture = useCallback(() => setForceBars(true), [setForceBars]);
  const { pdfLoading, downloadPDF } = usePdfExport(sheetRef, onBeforeCapture);

  const closeVideo = useCallback(() => setActiveVideo(null), [setActiveVideo]);

  return (
    <div className="min-h-screen py-6 px-3 sm:px-6 lg:py-10">
      {/* Action bar */}
      <div className="no-print max-w-[1000px] mx-auto mb-5 flex justify-end">
        <button
          onClick={downloadPDF}
          disabled={pdfLoading}
          className="inline-flex items-center gap-2 rounded-md bg-[var(--ink)] px-5 py-2.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-[var(--ink-dark)] disabled:opacity-70"
        >
          {pdfLoading ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
          {pdfLoading ? "Generating PDF…" : "Download PDF"}
        </button>
      </div>

      {/* Sheet */}
      <div ref={sheetRef} className="resume-sheet max-w-[1000px] mx-auto bg-white shadow-[0_20px_60px_-15px_rgba(30,32,80,0.25)] ring-1 ring-black/5">
        <ResumeHeader name={name} title={title} photo={photo} contact={contact} />

        <div className="h-[3px] bg-[var(--ink)] mx-5 sm:mx-8 lg:mx-12" />

        <div className="resume-body px-5 sm:px-8 lg:px-12 py-8 flex flex-col gap-y-8">
          <LeftColumn
            data={resumeData}
            skillsRef={skillsRef}
            barsIn={barsIn}
          />
          <RightColumn data={resumeData} />
          <VideosSection videos={resumeData.videos} onPlayVideo={setActiveVideo} />
        </div>

        <div className="h-2 bg-gradient-to-r from-[var(--ink)] to-[var(--ink-dark)]" />
      </div>

      <VideoModal videoId={activeVideo} onClose={closeVideo} />
    </div>
  );
};

export default Resume;
